import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { UserGroup } from "@/pages/Users";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Building2 } from "lucide-react";

const emptyCreate = { username: "", name: "", password: "", phone: "", address: "", email: "", security_question: "", security_answer: "" };

export default function Suppliers() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyCreate);
  const [editFor, setEditFor] = useState(null);
  const [eform, setEform] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/users").then((r) => setUsers(r.data.filter((u) => u.role === "supplier")));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.username || !form.name || !form.password) { toast.error("Lengkapi username, nama, dan password"); return; }
    if (form.password.length < 6) { toast.error("Password minimal 6 karakter"); return; }
    setBusy(true);
    try {
      await api.post("/users", { ...form, role: "supplier" });
      toast.success("Akun supplier berhasil dibuat");
      setOpen(false); setForm(emptyCreate); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const openEdit = (u) => {
    setEditFor(u);
    setEform({
      username: u.username, name: u.name, password: "", phone: u.phone || "",
      address: u.address || "", email: u.email || "", active: u.active,
      security_question: u.security_question || "", security_answer: "",
    });
  };

  const submitEdit = async () => {
    if (!eform.name.trim() || !eform.username.trim()) { toast.error("Nama dan username wajib diisi"); return; }
    if (eform.password && eform.password.length < 6) { toast.error("Password minimal 6 karakter"); return; }
    setBusy(true);
    try {
      const body = {
        username: eform.username.trim(), name: eform.name.trim(), phone: eform.phone,
        address: eform.address, email: eform.email, active: eform.active,
        security_question: eform.security_question,
      };
      if (eform.password) body.password = eform.password;
      if (eform.security_answer) body.security_answer = eform.security_answer;
      await api.patch(`/users/${editFor._id}`, body);
      toast.success("Akun supplier diperbarui");
      setEditFor(null); setEform(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u._id}`, { active: !u.active }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try {
      const res = await api.post("/deletion-requests", { resource_type: "user", resource_id: id, label: "akun supplier" });
      toast.success(res.data.pending ? "Permintaan hapus dikirim, menunggu persetujuan Owner" : "Akun supplier dihapus");
      load();
    }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4">
      <div data-testid="suppliers-header">
        <h2 className="font-heading text-lg sm:text-xl font-bold tracking-tight">Data Supplier</h2>
        <p className="text-xs sm:text-sm text-muted-foreground">Kelola akun pengguna supplier: tambah, edit, aktif/nonaktif, dan hapus.</p>
      </div>

      <UserGroup title="Supplier" icon={Building2} rows={users} isStaff={false} canManage groupId="supplier"
        onEdit={openEdit} onToggle={toggleActive} onDelete={remove}
        onAdd={() => { setForm(emptyCreate); setOpen(true); }} addLabel="Tambah Supplier" />

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setForm(emptyCreate); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-base">Tambah Akun Supplier</DialogTitle>
            <DialogDescription className="text-xs">Password disimpan aman (hashed) dan tidak ditampilkan.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-1">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">Username</Label><Input data-testid="supplier-username" className="h-9 text-xs" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} placeholder="cth. cv_mitra" /></div>
              <div className="space-y-1"><Label className="text-xs">Nama Supplier</Label><Input data-testid="supplier-name" className="h-9 text-xs" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            </div>
            <div className="space-y-1"><Label className="text-xs">Password</Label><Input data-testid="supplier-password" type="text" className="h-9 text-xs" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min. 6 karakter" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs">No. HP</Label><Input data-testid="supplier-phone" className="h-9 text-xs" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="08xx..." /></div>
              <div className="space-y-1"><Label className="text-xs">Email (opsional)</Label><Input data-testid="supplier-email" className="h-9 text-xs" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            </div>
            <div className="space-y-1"><Label className="text-xs">Alamat</Label><Input data-testid="supplier-address" className="h-9 text-xs" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
            <div className="rounded-lg border border-dashed border-border p-2.5 space-y-2">
              <p className="text-[11px] font-medium text-muted-foreground">Pertanyaan Keamanan (untuk Lupa Password)</p>
              <div className="space-y-1"><Label className="text-xs">Pertanyaan</Label><Input data-testid="supplier-sec-question" className="h-9 text-xs" value={form.security_question} onChange={(e) => setForm({ ...form, security_question: e.target.value })} placeholder="cth. Kota kelahiran?" /></div>
              <div className="space-y-1"><Label className="text-xs">Jawaban</Label><Input data-testid="supplier-sec-answer" className="h-9 text-xs" value={form.security_answer} onChange={(e) => setForm({ ...form, security_answer: e.target.value })} placeholder="Jawaban rahasia" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setOpen(false)}>Batal</Button>
            <Button onClick={submit} disabled={busy} data-testid="supplier-submit" variant="gold" size="sm" className="h-8 text-xs">Simpan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editFor} onOpenChange={(o) => !o && setEditFor(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-base">Edit Akun Supplier {editFor ? `— ${editFor.name}` : ""}</DialogTitle>
            <DialogDescription className="text-xs">Kosongkan kolom password bila tidak ingin mengubahnya.</DialogDescription>
          </DialogHeader>
          {editFor && eform && (
            <div className="space-y-3 py-1">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Nama</Label><Input data-testid="supplier-edit-name" className="h-9 text-xs" value={eform.name} onChange={(e) => setEform({ ...eform, name: e.target.value })} /></div>
                <div className="space-y-1"><Label className="text-xs">Username</Label><Input data-testid="supplier-edit-username" className="h-9 text-xs" value={eform.username} onChange={(e) => setEform({ ...eform, username: e.target.value })} /></div>
              </div>
              <div className="space-y-1"><Label className="text-xs">Password Baru (opsional)</Label><Input data-testid="supplier-edit-password" type="text" className="h-9 text-xs" value={eform.password} onChange={(e) => setEform({ ...eform, password: e.target.value })} placeholder="Biarkan kosong = password lama tetap" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">No. HP</Label><Input data-testid="supplier-edit-phone" className="h-9 text-xs" value={eform.phone} onChange={(e) => setEform({ ...eform, phone: e.target.value })} /></div>
                <div className="space-y-1"><Label className="text-xs">Email</Label><Input data-testid="supplier-edit-email" className="h-9 text-xs" value={eform.email} onChange={(e) => setEform({ ...eform, email: e.target.value })} /></div>
              </div>
              <div className="space-y-1"><Label className="text-xs">Alamat</Label><Input data-testid="supplier-edit-address" className="h-9 text-xs" value={eform.address} onChange={(e) => setEform({ ...eform, address: e.target.value })} /></div>
              <div className="rounded-lg border border-dashed border-border p-2.5 space-y-2">
                <p className="text-[11px] font-medium text-muted-foreground">Pertanyaan Keamanan {eform.security_question ? "(sudah diatur)" : "(belum diatur)"}</p>
                <div className="space-y-1"><Label className="text-xs">Pertanyaan</Label><Input data-testid="supplier-edit-sec-question" className="h-9 text-xs" value={eform.security_question} onChange={(e) => setEform({ ...eform, security_question: e.target.value })} /></div>
                <div className="space-y-1"><Label className="text-xs">Jawaban Baru (opsional)</Label><Input data-testid="supplier-edit-sec-answer" className="h-9 text-xs" value={eform.security_answer} onChange={(e) => setEform({ ...eform, security_answer: e.target.value })} placeholder="Kosongkan = jawaban lama tetap" /></div>
              </div>
              <div className="flex items-center justify-between rounded-lg border border-border p-2.5">
                <Label className="text-xs">Status Akun Aktif</Label>
                <Switch checked={eform.active} onCheckedChange={(v) => setEform({ ...eform, active: v })} data-testid="supplier-edit-active" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setEditFor(null)}>Batal</Button>
            <Button onClick={submitEdit} disabled={busy} data-testid="supplier-edit-submit" variant="gold" size="sm" className="h-8 text-xs">Simpan Perubahan</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
